import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { EnvironmentService } from '../../services/environment.service';
import {
  AutoMatchedResponse,
  BatchesResponse,
  BatchProgress,
  BRAND_ORG_TYPE_ID,
  BrandOrg,
  BulkConfirmResult,
  CountryOption,
  ImportConnectionsResult,
  OrgMatchSearchRequest,
  OrgMatchSearchResponse,
  OrgSearchResult,
  QueueItemsResponse,
  ReviewResponse,
  RegisterBrandRequest,
  UploadBatchResponse,
  UploadExcelParams
} from '../models/market-research.model';

/**
 * BOR service — market-research bulk matching.
 *
 * Different from the supergod/bhive services: it lives on its own host
 * ({@link EnvironmentService.borServiceUrl}) and returns raw payloads (no
 * {@link ApiResponse} envelope, so no `unwrap`). Authenticates with the
 * Keycloak bearer like the others (see `app.config.ts`'s
 * `INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG`); the region header below is
 * just routing, not auth — EU and CN share one Keycloak realm and one BOR
 * host today, so the backend can't tell them apart from the JWT alone.
 */
@Injectable({ providedIn: 'root' })
export class MarketResearchService {
  private readonly env = inject(EnvironmentService);
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${this.env.borServiceUrl}/api/v1/orgs/bulk_matching`;
  private readonly orgsUrl = `${this.env.borServiceUrl}/api/v1/organizations`;

  private get headers(): HttpHeaders {
    return new HttpHeaders({ 'X-BOR-Region': this.env.getRegion() });
  }

  /** All countries (bor_country), sorted by name — for the Register brand country picker. */
  getCountries(): Observable<CountryOption[]> {
    return this.http.get<CountryOption[]>(`${this.env.borServiceUrl}/api/v1/countries`, {
      headers: this.headers
    });
  }

  /**
   * Ranked "does this org already exist?" lookup via the matching engine. Used by Register
   * brand to warn about likely duplicates before a new brand is created.
   */
  searchSimilarOrgs(request: OrgMatchSearchRequest): Observable<OrgMatchSearchResponse> {
    return this.http.post<OrgMatchSearchResponse>(
      `${this.env.borServiceUrl}/api/v1/orgs/search`,
      request,
      { headers: this.headers }
    );
  }

  /**
   * Register a new non-BHive brand via the general POST /organizations. The backend
   * generates the BHive ID and returns the created org, which the upload then selects.
   */
  registerBrand(request: RegisterBrandRequest): Observable<BrandOrg> {
    return this.http.post<BrandOrg>(this.orgsUrl, request, { headers: this.headers });
  }

  /** Search brand organizations (orgTypeId = 1) by name for the brand picker. */
  searchBrands(orgName?: string, limit = 50): Observable<BrandOrg[]> {
    let httpParams = new HttpParams()
      .set('orgTypeId', BRAND_ORG_TYPE_ID)
      .set('limit', limit);
    if (orgName) {
      httpParams = httpParams.set('orgName', orgName);
    }
    return this.http.get<BrandOrg[]>(`${this.orgsUrl}/by-orgtype`, {
      headers: this.headers,
      params: httpParams
    });
  }

  /** Search any organization type by name — for manually matching a row to an account the suggestions missed. */
  searchAllOrgs(name: string, limit = 20): Observable<OrgSearchResult[]> {
    const httpParams = new HttpParams().set('name', name).set('limit', limit);
    return this.http.get<OrgSearchResult[]>(`${this.orgsUrl}/by-name`, {
      headers: this.headers,
      params: httpParams
    });
  }

  /** Upload the filled template; kicks off matching for a batch. */
  uploadExcel(file: File, params: UploadExcelParams): Observable<UploadBatchResponse> {
    const formData = new FormData();
    formData.append('file', file);
    let httpParams = new HttpParams()
      .set('brandId', params.brandId)
      .set('userName', params.userName);
    if (params.nonBhive != null) {
      httpParams = httpParams.set('nonBhive', params.nonBhive);
    }
    return this.http.post<UploadBatchResponse>(`${this.baseUrl}/upload_excel`, formData, {
      headers: this.headers,
      params: httpParams
    });
  }

  /** List existing batches by brand and/or date range (at least one required). */
  searchBatches(brandId?: string, fromDate?: string, toDate?: string): Observable<BatchesResponse> {
    let httpParams = new HttpParams();
    if (brandId) {
      httpParams = httpParams.set('brandId', brandId);
    }
    if (fromDate) {
      httpParams = httpParams.set('fromDate', fromDate);
    }
    if (toDate) {
      httpParams = httpParams.set('toDate', toDate);
    }
    return this.http.get<BatchesResponse>(`${this.baseUrl}/batches`, {
      headers: this.headers,
      params: httpParams
    });
  }

  getReviewItems(batchId: string): Observable<ReviewResponse> {
    return this.http.get<ReviewResponse>(`${this.baseUrl}/${batchId}/review`, {
      headers: this.headers
    });
  }

  getAutoMatchedItems(batchId: string): Observable<AutoMatchedResponse> {
    return this.http.get<AutoMatchedResponse>(`${this.baseUrl}/${batchId}/auto-matched`, {
      headers: this.headers
    });
  }

  /**
   * Rows by matching status (comma-separated), e.g. 'NO_MATCH,AUTO_REJECTED'. The review and
   * auto-matched routes each return one status only, so this is the only way to list rows
   * that have already been resolved.
   */
  /** Omit status to get every row in the batch regardless of matching status. */
  getItemsByStatus(batchId: string, status?: string): Observable<QueueItemsResponse> {
    let httpParams = new HttpParams();
    if (status) {
      httpParams = httpParams.set('status', status);
    }
    return this.http.get<QueueItemsResponse>(`${this.baseUrl}/${batchId}/rows`, {
      headers: this.headers,
      params: httpParams
    });
  }

  /** Confirm every auto-matched row scoring at or above minScore, in one call. */
  confirmAutoMatchedAbove(batchId: string, minScore: number): Observable<BulkConfirmResult> {
    const httpParams = new HttpParams().set('minScore', minScore);
    return this.http.post<BulkConfirmResult>(
      `${this.baseUrl}/${batchId}/confirm-auto-matched`,
      null,
      { headers: this.headers, params: httpParams }
    );
  }

  getProgress(batchId: string): Observable<BatchProgress> {
    return this.http.get<BatchProgress>(`${this.baseUrl}/${batchId}/progress`, {
      headers: this.headers
    });
  }

  /** Confirm a chosen suggestion for one review item. */
  selectSuggestion(queueId: number, bhiveId: string): Observable<unknown> {
    const httpParams = new HttpParams().set('queueId', queueId).set('bhiveId', bhiveId);
    return this.http.post<unknown>(`${this.baseUrl}/select`, null, {
      headers: this.headers,
      params: httpParams
    });
  }

  /**
   * Mark a queue item as NO_MATCH (none of the suggestions is correct).
   * Uses the `select` endpoint with the sentinel bhiveId `0`.
   */
  rejectMatch(queueId: number): Observable<unknown> {
    return this.selectSuggestion(queueId, '0');
  }

  /**
   * Persist the reviewed batch as connections. `userName` is recorded against the batch
   * so the export screen can say who imported it — same convention as upload_excel,
   * which also takes the Keycloak login id as a param.
   */
  importConnections(batchId: string, mode?: string, userName?: string): Observable<ImportConnectionsResult> {
    let httpParams = new HttpParams();
    if (mode) {
      httpParams = httpParams.set('mode', mode);
    }
    if (userName) {
      httpParams = httpParams.set('userName', userName);
    }
    return this.http.post<ImportConnectionsResult>(`${this.baseUrl}/${batchId}/import-connections`, null, {
      headers: this.headers,
      params: httpParams
    });
  }

  downloadTemplate(): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/template`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }

  downloadBatchResults(batchId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${batchId}/download`, {
      headers: this.headers,
      responseType: 'blob'
    });
  }
}
